import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FLEX_SCHEDULER_PERMISSIONS } from "../../../config/permissions";
import type { PermissionLocation } from "../../../features/user/user-slice";
import { fetchUserPermissions } from "../../../features/user/user-slice";
import { StoreType } from "../../../utils/types";

// Location ids where `code` may be used, from GET /Users/Permissions
// permissionLocations. A code that isn't listed, or a list that hasn't loaded
// yet, means allowed nowhere.
export const getPermittedLocationIds = (
	permissionLocations: PermissionLocation[] | null,
	code: string
): number[] => {
	const entry = permissionLocations?.find((item) => item?.code === code);
	const ids = Array.isArray(entry?.locationIds) ? entry.locationIds : [];
	return ids
		.map((id) => Number(id))
		.filter((id) => Number.isInteger(id) && id > 0);
};

// Direct Worker Assignment shows only for a Shift whose location is in the
// direct_assign_workers entry of permissionLocations. The backend has already
// applied the feature switch, the user's roles and the per-location rules to
// that list, so nothing else is checked here. The server checks it again on
// Publish.
const useCanDirectAssign = () => {
	const dispatch = useDispatch<any>();
	const permissionLocations = useSelector(
		(state: StoreType) => state.user.userData.permissionLocations
	);
	const permissionsLoadStatus = useSelector(
		(state: StoreType) => state.user.permissionsLoad.status
	);
	const hasRequestedLocations = useRef(false);

	// The login response carries permission codes but not where they apply, so
	// the list is missing until the first refresh. Ask for it once.
	useEffect(() => {
		if (
			permissionLocations === null &&
			permissionsLoadStatus !== "pending" &&
			!hasRequestedLocations.current
		) {
			hasRequestedLocations.current = true;
			dispatch(fetchUserPermissions());
		}
	}, [dispatch, permissionLocations, permissionsLoadStatus]);

	const permittedLocationIds = useMemo(
		() =>
			getPermittedLocationIds(
				permissionLocations,
				FLEX_SCHEDULER_PERMISSIONS.DIRECT_ASSIGN_WORKERS
			),
		[permissionLocations]
	);

	const hasDirectAssignPermission = permittedLocationIds.length > 0;

	const canDirectAssign = useCallback(
		(locationId: number | string | null | undefined) =>
			!!locationId && permittedLocationIds.includes(Number(locationId)),
		[permittedLocationIds]
	);

	return { hasDirectAssignPermission, canDirectAssign };
};

export default useCanDirectAssign;
